import { ResourceFragment } from "@graphql/resources/resources.g";
import React from "react";

import { AdminApplicationFilterFormValues } from "@components/Forms/AdminApplicationFilterForm/AdminApplicationFilterForm";
import { ApplicationFilterFormValues } from "@components/Forms/ApplicationFilterForm/ApplicationFilterForm";

import { Box } from "@design-system/Box/Box";
import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";

import {
  ADMIN_APPLICATION_TABLE_COLUMN_ORDER,
  ADMIN_APPLICATION_TABLE_COLUMN_SETTINGS,
  APPLICATION_TABLE_COLUMN_LABELS,
  ApplicationTableColumns,
  DEFAULT_APPLICATION_TABLE_COLUMN_ORDER,
  DEFAULT_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "./UI/ApplicationsTableColumns";
import { ExpandableResourceCell } from "./UI/ExpandableResourceCell";

/**
 * Default filter values for Applications
 */
export const DEFAULT_APPLICATION_FILTER_VALUES: ApplicationFilterFormValues = {
  vendorNameSearch: undefined,
  productNameSearch: undefined,
  productDomainNameSearch: undefined,
};

/**
 * Default filter values for Admin Applications
 */
export const DEFAULT_ADMIN_APPLICATION_FILTER_VALUES: AdminApplicationFilterFormValues =
  {
    vendorNameSearch: undefined,
    productNameSearch: undefined,
    productDomainNameSearch: undefined,
    isSaasFilter: "all",
  };

/**
 * Comprehensive table configuration for Applications
 */
export const ApplicationsTableConfig = createTableConfiguration<
  ResourceFragment,
  ApplicationTableColumns
>({
  tableId: "applications",
  columns: {
    [ApplicationTableColumns.productName]: {
      id: ApplicationTableColumns.productName,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.productName],
      fieldPath: "productName",
      sortField: "product_name",
      type: "custom",
      defaultVisible: true,
    },
    [ApplicationTableColumns.vendorName]: {
      id: ApplicationTableColumns.vendorName,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.vendorName],
      fieldPath: "vendorName",
      sortField: "vendor_name",
      type: "text",
      defaultVisible: true,
    },
    [ApplicationTableColumns.productDomain]: {
      id: ApplicationTableColumns.productDomain,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.productDomain],
      fieldPath: "productDomainName",
      sortField: "product_domain_name",
      type: "text",
      defaultVisible: false,
    },
    [ApplicationTableColumns.vendorDomain]: {
      id: ApplicationTableColumns.vendorDomain,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.vendorDomain],
      fieldPath: "vendorDomainName",
      sortField: "vendor_domain_name",
      type: "text",
      defaultVisible: false,
    },
    [ApplicationTableColumns.category]: {
      id: ApplicationTableColumns.category,
      label: APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.category],
      fieldPath: "category.name",
      sortable: false,
      type: "custom",
      defaultVisible: true,
    },
    [ApplicationTableColumns.mspVitalityScore]: {
      id: ApplicationTableColumns.mspVitalityScore,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[
          ApplicationTableColumns.mspVitalityScore
        ],
      fieldPath: "mspVitalityScore",
      sortField: "msp_vitality_score",
      type: "text",
      defaultVisible: true,
    },
    [ApplicationTableColumns.isForConsumers]: {
      id: ApplicationTableColumns.isForConsumers,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.isForConsumers],
      fieldPath: "isForConsumers",
      sortField: "is_for_consumers",
      type: "custom",
      defaultVisible: false,
    },
    [ApplicationTableColumns.isSaas]: {
      id: ApplicationTableColumns.isSaas,
      label: APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.isSaas],
      fieldPath: "isSaas",
      sortField: "is_saas",
      type: "custom",
      defaultVisible: false,
    },
    [ApplicationTableColumns.isVisible]: {
      id: ApplicationTableColumns.isVisible,
      label: APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.isVisible],
      fieldPath: "isVisible",
      sortField: "is_visible",
      type: "custom",
      defaultVisible: false,
    },
    [ApplicationTableColumns.createdAt]: {
      id: ApplicationTableColumns.createdAt,
      label: APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.createdAt],
      fieldPath: "createdAt",
      sortField: "created_at",
      type: "date",
      defaultVisible: false,
    },
    [ApplicationTableColumns.updatedAt]: {
      id: ApplicationTableColumns.updatedAt,
      label: APPLICATION_TABLE_COLUMN_LABELS[ApplicationTableColumns.updatedAt],
      fieldPath: "updatedAt",
      sortField: "updated_at",
      type: "date",
      defaultVisible: false,
    },
    [ApplicationTableColumns.productDescription]: {
      id: ApplicationTableColumns.productDescription,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[
          ApplicationTableColumns.productDescription
        ],
      fieldPath: "productDescription",
      sortable: false,
      type: "text",
      defaultVisible: false,
    },
    [ApplicationTableColumns.vendorDescription]: {
      id: ApplicationTableColumns.vendorDescription,
      label:
        APPLICATION_TABLE_COLUMN_LABELS[
          ApplicationTableColumns.vendorDescription
        ],
      fieldPath: "vendorDescription",
      sortable: false,
      type: "text",
      defaultVisible: false,
    },
  },
  defaultColumnOrder: DEFAULT_APPLICATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_APPLICATION_TABLE_COLUMN_SETTINGS,
});

/**
 * Administrative table configuration with Is SaaS column visible
 */
export const AdminApplicationsTableConfig = createTableConfiguration<
  ResourceFragment,
  ApplicationTableColumns
>({
  tableId: "admin-applications",
  columns: ApplicationsTableConfig.columns,
  defaultColumnOrder: ADMIN_APPLICATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: ADMIN_APPLICATION_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = (): ApplicationFilterFormValues => ({
  ...DEFAULT_APPLICATION_FILTER_VALUES,
});

/**
 * Helper function to get default admin filter values
 */
export const getDefaultAdminFilterValues =
  (): AdminApplicationFilterFormValues => ({
    ...DEFAULT_ADMIN_APPLICATION_FILTER_VALUES,
  });

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (
  filterValues: ApplicationFilterFormValues,
): boolean => {
  return Boolean(
    filterValues.vendorNameSearch ||
      filterValues.productNameSearch ||
      filterValues.productDomainNameSearch,
  );
};

/**
 * Helper function to check if admin filters are active
 */
export const areAdminFiltersActive = (
  filterValues: AdminApplicationFilterFormValues,
): boolean => {
  return Boolean(
    filterValues.vendorNameSearch ||
      filterValues.productNameSearch ||
      filterValues.productDomainNameSearch ||
      (filterValues.isSaasFilter && filterValues.isSaasFilter !== "all") ||
      filterValues.categoryId,
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderProductName(
  value: unknown,
  row: ResourceFragment,
  expansionProps?: {
    isExpanded: boolean;
    onToggleExpansion: () => void;
    isNestedResource?: boolean;
  },
): React.ReactNode {
  const additionalResourcesCount = row.nonPrimaryResourcesCount || 0;

  return (
    <ExpandableResourceCell
      name={(value as string) ?? ""}
      subtext={row.vendorName || undefined}
      signedLogoUrl={row.signedLogoUrl || undefined}
      domain={row.productDomainName ?? ""}
      additionalResourcesCount={additionalResourcesCount}
      isExpanded={expansionProps?.isExpanded || false}
      onToggleExpansion={expansionProps?.onToggleExpansion || (() => {})}
      isNestedResource={expansionProps?.isNestedResource || false}
    />
  );
}

export function renderVendorName(value: unknown): React.ReactNode {
  return <Text>{String(value) || "-"}</Text>;
}

export function renderCategory(
  _value: unknown,
  row: ResourceFragment,
): React.ReactNode {
  // Show parent category if it exists, otherwise show the category itself
  const category = row.category;
  const categoryName = category?.parent?.name || category?.name;
  return (
    <Box>
      {categoryName ? (
        <Tag color="secondary" text={String(categoryName)} />
      ) : (
        <Text>-</Text>
      )}
    </Box>
  );
}

export function renderMspVitalityScore(value: unknown): React.ReactNode {
  return <Text>{String(value) || "-"}</Text>;
}

export function renderIsForConsumers(value: unknown): React.ReactNode {
  const isForConsumers = value as boolean;
  return (
    <Tag
      color={isForConsumers ? "success" : "secondary"}
      text={isForConsumers ? "Yes" : "No"}
    />
  );
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderUpdatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderProductDescription(value: unknown): React.ReactNode {
  return (
    <Text truncate maxWidth={400} title={String(value) || undefined}>
      {String(value) || "-"}
    </Text>
  );
}

export function renderVendorDescription(value: unknown): React.ReactNode {
  return (
    <Text truncate maxWidth={400} title={String(value) || undefined}>
      {String(value) || "-"}
    </Text>
  );
}

export function renderIsSaas(value: unknown): React.ReactNode {
  const isSaas = value as boolean;
  return (
    <Tag color={isSaas ? "success" : "warning"} text={isSaas ? "Yes" : "No"} />
  );
}

export function renderIsVisible(value: unknown): React.ReactNode {
  const isVisible = value as boolean;
  return (
    <Tag
      color={isVisible ? "success" : "warning"}
      text={isVisible ? "Visible" : "Hidden"}
    />
  );
}
